// =====================================================
// FuMind_GM HR Module - Employee Service
// Phase 3F.16 + Profile Verification Integrated
// =====================================================

const HR_EMPLOYEE_MASTER_SAVE_FLOW_URL = "/tracka-flow/v1/HR_EMPLOYEE_MASTER_SAVE";
const HR_EMPLOYEE_MASTER_GET_ALL_FLOW_URL = "/tracka-flow/v1/HR_EMPLOYEE_MASTER_GET_ALL";
const HR_EMPLOYEE_MASTER_GET_BY_EMAIL_FLOW_URL = "/tracka-flow/v1/HR_EMPLOYEE_MASTER_GET_BY_EMAIL";

const HR_PROFILE_VERIFY_FLOW_URL = "/tracka-flow/v1/HR_PROFILE_VERIFY";
const HR_SETTINGS_MASTERDATA_FLOW_URL = "/tracka-flow/v1/HR_SETTINGS_MASTERDATA";

const HR_EMPLOYEE_CURRENT_ASSIGNMENT_FLOW_URL = "/tracka-flow/v1/HR_EMPLOYEE_CURRENT_ASSIGNMENT";
const HR_EMPLOYEE_POLICY_HISTORY_FLOW_URL = "/tracka-flow/v1/HR_EMPLOYEE_POLICY_HISTORY";
const API_KEY = "";

// =====================================================
// Common Helpers
// =====================================================

async function parseJsonResponse(response) {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { message: text, rawText: text };
  }
}

function normalizePowerAutomateResponse(rawData) {
  return rawData?.body || rawData || {};
}

function throwIfFlowFailed(response, data, fallbackMessage) {
  if (!response.ok || data?.success === false) {
    const flowMessage =
      data?.message || data?.error?.message || data?.rawText || "";
    const statusText = `${response.status} ${response.statusText}`.trim();

    throw new Error(
      flowMessage
        ? `${fallbackMessage}: ${flowMessage}`
        : `${fallbackMessage}. HTTP ${statusText}`
    );
  }
}

// =====================================================
// ✅ SAVE EMPLOYEE MASTER (Phase 3F.16 FULL PAYLOAD)
// =====================================================

export async function saveEmployeeMaster(employeeData = {}) {
  const payload = {

    mode: employeeData.id || employeeData.ID ? "update" : "create",

    id: String(employeeData.id || employeeData.ID || ""),
    ID: String(employeeData.ID || employeeData.id || ""),

    // ===============================
    // Identity
    // ===============================
    employeeId: employeeData.employeeId || "",
    workEmail: employeeData.workEmail || "",

    // ===============================
    // Organization
    // ===============================
    legalEntity: employeeData.legalEntity || "",
    legalEntityCode: employeeData.legalEntityCode || "",

    country: employeeData.country || "India",
    countryCode: employeeData.countryCode || "IN",
    regionState: employeeData.regionState || "",

    locationCode:
      employeeData.locationCode ||
      employeeData.workLocationId ||
      "",

    workLocationId:
      employeeData.workLocationId ||
      employeeData.locationCode ||
      "",

    industryType:
      employeeData.industryType || "Office / IT / Services",

    department: employeeData.department || "",
    departmentCode: employeeData.departmentCode || "",

    // Work Unit / Production Unit - keep both for backward compatibility.
    workUnitCode:
      employeeData.workUnitCode ||
      employeeData.productionUnitCode ||
      "",

    // ✅ backward compatibility for existing flow/body mappings
    productionUnitCode:
      employeeData.productionUnitCode ||
      employeeData.workUnitCode ||
      "",

    designation: employeeData.designation || "",
    gradeLevel: employeeData.gradeLevel || "",

    reportingManager: employeeData.reportingManager || "",
    managerEmployeeCode: employeeData.managerEmployeeCode || "",

    joiningDate: employeeData.joiningDate || null,
    employmentStatus: employeeData.employmentStatus || "Offered",

    // ===============================
    // Employment
    // ===============================
    employmentType: employeeData.employmentType || "Full Time",
    workArrangement: employeeData.workArrangement || "Office",
    ftePercent: String(employeeData.ftePercent || "100"),

    timeZone:
      employeeData.timeZone ||
      (employeeData.country === "Germany"
        ? "Europe/Berlin"
        : "Asia/Kolkata"),

    // ===============================
    // Policy Assignment (IMPORTANT)
    // ===============================
    leavePolicyId: employeeData.leavePolicyId || "",

    attendancePolicyId: employeeData.attendancePolicyId || "",

    shiftPatternId:
      employeeData.shiftPatternId ||
      employeeData.shiftPolicyId ||
      "",

    weeklyOffPatternId:
      employeeData.weeklyOffPatternId || "",

    // ✅ backward compatibility
    shiftPolicyId:
      employeeData.shiftPolicyId ||
      employeeData.shiftPatternId ||
      "",

    holidayCalendarId:
      employeeData.holidayCalendarId || "",

    // ===============================
    // Compensation
    // ===============================
    payGrade: employeeData.payGrade || "",
    salaryCurrency: employeeData.salaryCurrency || "INR",
    compensationStatus:
      employeeData.compensationStatus || "Pending Entry",

    // ===============================
    // Current CTC Summary (owned by CTC approval sync)
    // These fields are optional during EmployeeMaster manual save.
    // ===============================
    currentEmployeeCTCId: employeeData.currentEmployeeCTCId || "",
    currentCTCStructureId: employeeData.currentCTCStructureId || "",
    currentCTCEffectiveFrom: employeeData.currentCTCEffectiveFrom || null,
    currentCTCStatus: employeeData.currentCTCStatus || "",
    annualCTC: employeeData.annualCTC || "",
    monthlyCTC: employeeData.monthlyCTC || "",
    monthlyGross: employeeData.monthlyGross || "",
    annualEmployerCost: employeeData.annualEmployerCost || "",
    netPayEstimateMonthly: employeeData.netPayEstimateMonthly || "",
    cTCLastApprovedOn: employeeData.cTCLastApprovedOn || null,
    cTCRevisionNumber: employeeData.cTCRevisionNumber || "",
    revisionDueFlag: Boolean(employeeData.revisionDueFlag || false),

    // ===============================
    // Profile Tracking (IMPORTANT)
    // ===============================
    profileCompletionStatus:
      employeeData.profileCompletionStatus ||
      "Not Started",

    profileVerificationStatus:
      employeeData.profileVerificationStatus ||
      "Not Verified",

    onboardingStage:
      employeeData.onboardingStage || "Preboarding",
  };

  const response = await fetch(HR_EMPLOYEE_MASTER_SAVE_FLOW_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const rawData = await parseJsonResponse(response);
  const data = normalizePowerAutomateResponse(rawData);

  throwIfFlowFailed(response, data, "Failed to save employee master");

  return data;
}

// =====================================================
// GET ALL
// =====================================================

export async function getEmployeeMasters() {
  const response = await fetch(HR_EMPLOYEE_MASTER_GET_ALL_FLOW_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });

  const rawData = await parseJsonResponse(response);
  const data = normalizePowerAutomateResponse(rawData);

  throwIfFlowFailed(response, data, "Failed to load employees");

  return Array.isArray(data.employees) ? data.employees : [];
}

// =====================================================
// GET BY EMAIL
// =====================================================

export async function getEmployeeMasterByEmail(workEmail) {
  const response = await fetch(HR_EMPLOYEE_MASTER_GET_BY_EMAIL_FLOW_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      email: workEmail,
    }),
  });

  const rawData = await parseJsonResponse(response);
  const data = normalizePowerAutomateResponse(rawData);

  throwIfFlowFailed(response, data, "Employee profile not found");

  const employeeRecord = Array.isArray(data.employee)
    ? data.employee[0]
    : data.employee;

  if (!employeeRecord) {
    throw new Error("Employee record not found.");
  }

  return {
    success: true,
    employee: employeeRecord,
  };
}

// =====================================================
// ✅ PROFILE VERIFY (RESTORED - IMPORTANT)
// =====================================================

export async function verifyEmployeeProfile({
  employeeId,
  workEmail,
  action,
  comments = "",
  verifiedBy = "",
} = {}) {
  if (!HR_PROFILE_VERIFY_FLOW_URL) {
    throw new Error("Profile Verify flow URL not configured.");
  }

  if (!employeeId) {
    throw new Error("Employee ID is required.");
  }

  const normalizedAction = String(action || "").toLowerCase();

  if (!["approve", "reject"].includes(normalizedAction)) {
    throw new Error("Invalid action. Use approve or reject.");
  }

  if (normalizedAction === "reject" && !comments.trim()) {
    throw new Error("Comments required for rejection.");
  }

  const payload = {

    employeeId,
    workEmail: workEmail || "",
    action: normalizedAction,
    comments,
    verifiedBy,
  };

  const response = await fetch(HR_PROFILE_VERIFY_FLOW_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const rawData = await parseJsonResponse(response);
  const data = normalizePowerAutomateResponse(rawData);

  throwIfFlowFailed(response, data, "Profile verification failed");

  return data;
}


export async function getHRSettingsMasterData() {
  const response = await fetch(HR_SETTINGS_MASTERDATA_FLOW_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      payload: { includeInactive: true },
    }),
  });

  const rawData = await parseJsonResponse(response);
  const data = normalizePowerAutomateResponse(rawData);
  throwIfFlowFailed(response, data, "Failed to load HR settings master data");
  return data;
}

// =====================================================
// Phase 3F.17D - Employee Policy Assignment Helpers
// =====================================================
function normalizePolicyAssignmentRecord(record = {}) {
  if (!record || typeof record !== "object") return null;
  const get = (...names) => {
    for (const name of names) {
      if (record[name] !== undefined && record[name] !== null) return record[name];
    }
    return "";
  };

  return {
    id: String(get("id", "ID", "ItemInternalId") || ""),
    ID: String(get("ID", "id", "ItemInternalId") || ""),
    title: get("Title") || "",
    employeeCode: get("EmployeeCode", "field_1") || "",
    leavePolicyId: get("LeavePolicyId", "field_2") || "",
    attendancePolicyId: get("AttendancePolicyId", "field_3") || "",
    shiftPatternId: get("ShiftPatternId", "field_4") || "",
    weeklyOffPatternId: get("WeeklyOffPatternId", "field_5") || "",
    holidayCalendarId: get("HolidayCalendarId", "field_6") || "",
    legalEntityCode: get("LegalEntityCode", "field_7") || "",
    locationCode: get("LocationCode", "field_8") || "",
    departmentCode: get("DepartmentCode", "field_9") || "",
    workUnitCode: get("WorkUnitCode", "field_10") || "",
    effectiveFrom: get("EffectiveFrom", "field_11") || "",
    effectiveTo: get("EffectiveTo", "field_12") || "",
    assignedBy: get("AssignedBy", "field_13") || "",
    assignmentReason: get("AssignmentReason", "field_14") || "",
    isCurrent: get("IsCurrent", "field_15") === true || get("IsCurrent", "field_15") === "Yes" || get("IsCurrent", "field_15") === "true",
    isActive: get("IsActive", "field_16") === true || get("IsActive", "field_16") === "Yes" || get("IsActive", "field_16") === "true",
    created: get("Created") || "",
    modified: get("Modified") || "",
  };
}

export async function getCurrentEmployeePolicyAssignment(employeeId) {
  if (!employeeId) return null;
  if (!HR_EMPLOYEE_CURRENT_ASSIGNMENT_FLOW_URL || HR_EMPLOYEE_CURRENT_ASSIGNMENT_FLOW_URL.includes("PASTE_")) {
    throw new Error("Get Current Employee Policy Assignment flow URL not configured.");
  }

  const response = await fetch(HR_EMPLOYEE_CURRENT_ASSIGNMENT_FLOW_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ employeeId }),
  });

  const rawData = await parseJsonResponse(response);
  const data = normalizePowerAutomateResponse(rawData);
  throwIfFlowFailed(response, data, "Failed to load current employee policy assignment");

  const assignment = Array.isArray(data.assignment)
    ? data.assignment[0]
    : data.assignment || (Array.isArray(data.assignments) ? data.assignments[0] : null);

  return assignment ? normalizePolicyAssignmentRecord(assignment) : null;
}

export async function getEmployeePolicyHistory(employeeId) {
  if (!employeeId) return [];
  if (!HR_EMPLOYEE_POLICY_HISTORY_FLOW_URL || HR_EMPLOYEE_POLICY_HISTORY_FLOW_URL.includes("PASTE_")) {
    throw new Error("Get Employee Policy History flow URL not configured.");
  }

  const response = await fetch(HR_EMPLOYEE_POLICY_HISTORY_FLOW_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ employeeId }),
  });

  const rawData = await parseJsonResponse(response);
  const data = normalizePowerAutomateResponse(rawData);
  throwIfFlowFailed(response, data, "Failed to load employee policy history");

  const rows = Array.isArray(data.history)
    ? data.history
    : Array.isArray(data.assignments)
      ? data.assignments
      : [];

  return rows.map(normalizePolicyAssignmentRecord).filter(Boolean);
}
